import { useReactFlow } from '@xyflow/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { ExternalTransferNodeData } from '../../../converters/shared/reactFlowTypes'
import {
  ExternalTransferPhoneNumberDigitLimits,
  isValidExternalTransferPhoneNumber,
  normalizeExternalTransferPhoneNumber,
} from '../../../utils/externalTransferPhoneNumberValidation'

/**
 * Hook for External Transfer node functionality.
 * Handles phone number, timeout, SIP server, caller ID options, and require 1 to connect.
 */
export const useExternalTransferNode = (
  id: string,
  data: ExternalTransferNodeData,
  phoneNumberDigitLimits: ExternalTransferPhoneNumberDigitLimits,
  phoneNumberLengthErrorMessage: string,
) => {
  const { updateNodeData } = useReactFlow()
  const dataRef = useRef(data)
  const isUpdatingRef = useRef(false)
  const hasSaveTimePhoneNumberErrorRef = useRef(false)

  // Keep dataRef in sync with data
  useEffect(() => {
    dataRef.current = data
  }, [data])

  // Local state for inputs (to prevent focus loss)
  const [phoneNumber, setPhoneNumber] = useState(data.phoneNumber || '')
  const [timeout, setTimeoutValue] = useState(
    data.timeout !== undefined ? String(data.timeout) : '30',
  )
  const [sipServer, setSipServer] = useState(data.sipServer || '')
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null)

  // Read save-time validation errors from node data
  // Save-time errors take precedence over format errors
  useEffect(() => {
    const validationErrors = data.validationErrors as
      Record<string, string> | undefined
    if (validationErrors?.phoneNumber) {
      hasSaveTimePhoneNumberErrorRef.current = true
      setPhoneNumberError(validationErrors.phoneNumber)
    } else if (hasSaveTimePhoneNumberErrorRef.current) {
      hasSaveTimePhoneNumberErrorRef.current = false
      setPhoneNumberError(null)
    }
  }, [data.validationErrors])

  // Sync local state from external data (only when not updating)
  useEffect(() => {
    if (!isUpdatingRef.current) {
      setPhoneNumber(data.phoneNumber || '')
      setTimeoutValue(data.timeout !== undefined ? String(data.timeout) : '30')
      setSipServer(data.sipServer || '')
    }
  }, [data.phoneNumber, data.timeout, data.sipServer])

  const currentPassThroughCallerId = data.passThroughCallerId ?? true
  // Use phoneNumberId (ID) if available, otherwise fall back to callerId (backward compatibility)
  const currentPhoneNumberId = data.phoneNumberId || data.callerId || ''
  const currentTransferCompany = data.transferCompany || ''
  // Default to true to match legacy behavior (new nodes have checkbox checked by default)
  const currentRequireOneToConnect = data.requireOneToConnect ?? true
  const currentMinimized = data.minimized ?? false

  const handlePhoneNumberChange = useCallback(
    (value: string) => {
      isUpdatingRef.current = true
      setPhoneNumber(value)

      const normalizedPhoneNumber = normalizeExternalTransferPhoneNumber(value)
      const isValidPhoneNumber = isValidExternalTransferPhoneNumber(
        normalizedPhoneNumber,
        phoneNumberDigitLimits,
      )
      const validationErrors = dataRef.current.validationErrors
      const hasPhoneNumberValidationError = Boolean(
        validationErrors?.phoneNumber,
      )

      if (isValidPhoneNumber || hasPhoneNumberValidationError) {
        const updatedErrors = { ...validationErrors }
        delete updatedErrors.phoneNumber
        const updatedData = {
          ...dataRef.current,
          ...(isValidPhoneNumber ? { phoneNumber: normalizedPhoneNumber } : {}),
          validationErrors:
            Object.keys(updatedErrors).length > 0 ? updatedErrors : undefined,
        }

        dataRef.current = updatedData
        updateNodeData(id, updatedData)

        if (hasPhoneNumberValidationError) {
          hasSaveTimePhoneNumberErrorRef.current = false
          setPhoneNumberError(null)
        }
      }

      if (isValidPhoneNumber) {
        isUpdatingRef.current = false
      }
    },
    [id, phoneNumberDigitLimits, updateNodeData],
  )

  const handlePhoneNumberBlur = useCallback(() => {
    const normalizedPhoneNumber =
      normalizeExternalTransferPhoneNumber(phoneNumber)
    const isValidPhoneNumber = isValidExternalTransferPhoneNumber(
      normalizedPhoneNumber,
      phoneNumberDigitLimits,
    )
    let error: string | null = null

    if (phoneNumber.trim() && !isValidPhoneNumber) {
      error = phoneNumberLengthErrorMessage
    }

    if (isValidPhoneNumber) {
      setPhoneNumber(normalizedPhoneNumber)
    }

    // Clear save-time validation error when user makes changes
    if (phoneNumber && dataRef.current.validationErrors?.phoneNumber) {
      const updatedErrors = { ...dataRef.current.validationErrors }
      delete updatedErrors.phoneNumber
      updateNodeData(id, {
        ...dataRef.current,
        phoneNumber: isValidPhoneNumber
          ? normalizedPhoneNumber
          : phoneNumber || undefined,
        validationErrors:
          Object.keys(updatedErrors).length > 0 ? updatedErrors : undefined,
      })
      setPhoneNumberError(error)
      isUpdatingRef.current = false
      return
    }

    setPhoneNumberError(error)

    const cleanedPhoneNumber = isValidPhoneNumber
      ? normalizedPhoneNumber
      : phoneNumber || undefined

    updateNodeData(id, {
      ...dataRef.current,
      phoneNumber: cleanedPhoneNumber,
    })
    isUpdatingRef.current = false
  }, [
    id,
    phoneNumber,
    phoneNumberDigitLimits,
    phoneNumberLengthErrorMessage,
    updateNodeData,
  ])

  const handleTimeoutChange = useCallback((value: string) => {
    isUpdatingRef.current = true
    setTimeoutValue(value)
  }, [])

  const handleTimeoutBlur = useCallback(() => {
    const timeoutValue = timeout ? parseInt(timeout, 10) : undefined
    // Clamp to valid range: 5-300
    const clampedValue =
      timeoutValue && !isNaN(timeoutValue)
        ? Math.max(5, Math.min(300, timeoutValue))
        : undefined
    updateNodeData(id, {
      ...dataRef.current,
      timeout: clampedValue || undefined,
    })
    isUpdatingRef.current = false
  }, [id, updateNodeData, timeout])

  const handlePassThroughCallerIdChange = useCallback(
    (checked: boolean) => {
      updateNodeData(id, {
        ...dataRef.current,
        passThroughCallerId: checked,
      })
    },
    [id, updateNodeData],
  )

  const handlePhoneNumberIdChange = useCallback(
    (phoneNumberId: string | undefined) => {
      updateNodeData(id, {
        ...dataRef.current,
        phoneNumberId,
        // Clear deprecated callerId when setting phoneNumberId
        ...(phoneNumberId ? { callerId: undefined } : {}),
      })
    },
    [id, updateNodeData],
  )

  const handleTransferCompanyChange = useCallback(
    (transferCompany: string | undefined) => {
      updateNodeData(id, {
        ...dataRef.current,
        transferCompany: transferCompany || undefined,
      })
    },
    [id, updateNodeData],
  )

  const handleRequireOneToConnectChange = useCallback(
    (checked: boolean) => {
      updateNodeData(id, {
        ...dataRef.current,
        requireOneToConnect: checked,
      })
    },
    [id, updateNodeData],
  )

  const handleMinimizedChange = useCallback(
    (minimized: boolean) => {
      updateNodeData(id, {
        ...dataRef.current,
        minimized,
      })
    },
    [id, updateNodeData],
  )

  return {
    phoneNumber,
    timeout,
    sipServer,
    currentPassThroughCallerId,
    currentPhoneNumberId,
    currentTransferCompany,
    currentRequireOneToConnect,
    handlePhoneNumberChange,
    handlePhoneNumberBlur,
    handleTimeoutChange,
    handleTimeoutBlur,
    handlePassThroughCallerIdChange,
    handlePhoneNumberIdChange,
    handleTransferCompanyChange,
    handleRequireOneToConnectChange,
    currentMinimized,
    handleMinimizedChange,
    phoneNumberError,
    hasData: !!phoneNumber || !!timeout || !!sipServer,
  }
}
